import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS, hasPermission, UserRole } from "../types/church.js";
import Icon from "./Icon";
import perfilService from "../services/perfilService";
import notificationService from "../services/notificationService";
import Modal, { useModal } from "./Modal";
import ThemeSelector from "./ThemeSelector";
import { useTheme } from "../hooks/useTheme";

const Header = () => {
  const { currentUser, churchUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState(
    notificationService.getPermissionStatus()
  );
  const { showAlert, showLoading, hideLoading, ModalComponents } = useModal();
  useTheme(); // mantém o contexto ativo

  // Verificar se é usuário Google (pode editar perfil)
  const isGoogleUser = currentUser?.metodoAuth !== "cpf";

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleProfileClick = () => {
    navigate("/perfil");
    setShowDropdown(false);
  };

  const handleLogoutClick = () => {
    setShowDropdown(false);
    handleLogout();
  };

  const handleActivateNotifications = async () => {
    setShowDropdown(false);

    if (!notificationService.isNotificationSupported()) {
      await showAlert({
        variant: "error",
        title: "Não Suportado",
        message: "Seu navegador não suporta notificações push.",
      });
      return;
    }

    const currentPermission = notificationService.getPermissionStatus();

    if (currentPermission === "denied") {
      await showAlert({
        variant: "warning",
        title: "Permissão Negada",
        message:
          "Você bloqueou as notificações. Para ativar, vá nas configurações do navegador e permita notificações para este site.",
      });
      return;
    }

    if (
      currentPermission === "granted" &&
      notificationService.isTokenRegistered()
    ) {
      await showAlert({
        variant: "info",
        title: "Notificações Ativas",
        message: "As notificações já estão ativadas para este dispositivo.",
      });
      return;
    }

    try {
      showLoading("Ativando notificações...");

      const token = await notificationService.requestPermissionAndGetToken(
        currentUser.uid
      );

      hideLoading();

      if (token) {
        setNotificationStatus("granted");
        await showAlert({
          variant: "success",
          title: "Notificações Ativadas",
          message:
            "Você receberá notificações importantes da igreja neste dispositivo!",
        });

        // Subscrever ao tópico de todos os membros
        await notificationService.subscribeToTopic(
          currentUser.uid,
          "all_members"
        );
      } else {
        await showAlert({
          variant: "error",
          title: "Erro ao Ativar",
          message:
            "Não foi possível ativar as notificações. Tente novamente mais tarde.",
        });
      }
    } catch (error) {
      hideLoading();
      console.error("[Header] Erro ao ativar notificações:", error);
      await showAlert({
        variant: "error",
        title: "Erro",
        message: "Ocorreu um erro ao ativar as notificações.",
      });
    }
  };

  const getNotificationIcon = () => {
    const status = notificationService.getPermissionStatus();
    if (status === "granted" && notificationService.isTokenRegistered()) {
      return "bell";
    } else if (status === "denied") {
      return "bell-off";
    } else {
      return "bell";
    }
  };

  const getNotificationText = () => {
    const status = notificationService.getPermissionStatus();
    if (status === "granted" && notificationService.isTokenRegistered()) {
      return "Notificações Ativas";
    } else if (status === "denied") {
      return "Notificações Bloqueadas";
    } else {
      return "Ativar Notificações";
    }
  };

  if (!currentUser || !churchUser) {
    return null; // Não mostra header para usuários não logados
  }

  const navItemStyle = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "0.65rem",
    padding: "0.55rem 0.5rem",
    borderRadius: "var(--radius-md)",
    border: "none",
    backgroundColor: "transparent",
    color: "var(--color-text)",
    cursor: "pointer",
    textAlign: "left",
  };

  return (
    <header
      className="card"
      style={{
        borderRadius: 0,
        borderLeft: 0,
        borderRight: 0,
        borderTop: 0,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          {/* Lado esquerdo - Botão voltar e título */}
          <div className="d-flex align-items-center gap-3">
            <button
              onClick={handleGoBack}
              className="btn btn-outline btn-sm"
              title="Voltar à página anterior"
            >
              <Icon name="arrow-left" size={16} />
            </button>
            <img
              src="/icons/logo.png"
              alt="Ice Siqueira"
              style={{ height: 36, cursor: "pointer" }}
              onClick={() => navigate("/dashboard")}
              title="Ir para Início"
            />
          </div>

          {/* Centro - Links de navegação */}
          <nav className="d-none d-md-flex align-items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn btn-outline btn-sm"
            >
              <Icon name="home" size={16} className="me-1" />
              Início
            </button>

            <button
              onClick={() => navigate("/biblia")}
              className="btn btn-outline btn-sm"
            >
              <Icon name="book-open" size={16} className="me-1" />
              Bíblia
            </button>

            {hasPermission(churchUser.role, UserRole.SEGUNDO_TESOUREIRO) && (
              <button
                onClick={() => navigate("/financeiro")}
                className="btn btn-outline btn-sm"
              >
                <Icon name="dollar-sign" size={16} className="me-1" />
                Financeiro
              </button>
            )}

            {hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) && (
              <button
                onClick={() => navigate("/membros")}
                className="btn btn-outline btn-sm"
              >
                <Icon name="users" size={16} className="me-1" />
                Membros
              </button>
            )}


          </nav>

          {/* Lado direito - Menu do usuário */}
          <div className="d-flex align-items-center gap-2">
            {/* Menu do usuário */}
            <div className="position-relative" ref={dropdownRef}>
            <div
              className="user-menu-trigger d-flex align-items-center gap-2"
              onClick={() => {
                setShowThemeModal(false);
                setShowDropdown(!showDropdown);
              }}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setShowDropdown(!showDropdown);
                }
              }}
            >
              {/* Foto do usuário */}
              <div
                className="user-photo"
                style={{ width: "32px", height: "32px" }}
              >
                {perfilService.getPhotoURL(currentUser, churchUser) ? (
                  <img
                    src={perfilService.getPhotoURL(currentUser, churchUser)}
                    alt={perfilService.getDisplayName(currentUser, churchUser)}
                    onError={(e) => {
                      // Prevenir propagação do erro para evitar log desnecessário
                      e.preventDefault();
                      e.stopPropagation();
                      // Ocultar imagem quebrada e mostrar fallback
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                    style={{ display: "block" }}
                  />
                ) : null}
                <div
                  className="user-photo-fallback"
                  style={{
                    display: perfilService.getPhotoURL(currentUser, churchUser)
                      ? "none"
                      : "flex",
                  }}
                >
                  {perfilService
                    .getDisplayName(currentUser, churchUser)
                    .charAt(0)
                    .toUpperCase()}
                </div>
              </div>

              {/* Nome e função */}
              <div className="text-start d-none d-md-block">
                <div
                  className="text-sm"
                  style={{ fontWeight: "600", lineHeight: "1.2" }}
                >
                  {perfilService.getDisplayName(currentUser, churchUser)}
                </div>
                <div
                  className="text-xs"
                  style={{ color: "var(--color-primary)", lineHeight: "1.2" }}
                >
                  {ROLE_LABELS[churchUser?.role] || "Membro"}
                </div>
              </div>

              {/* Ícone dropdown */}
              <Icon
                name={showDropdown ? "ChevronUp" : "ChevronDown"}
                size={16}
                className="text-muted"
              />
            </div>

            {/* Menu Dropdown */}
            {showDropdown && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    zIndex: 1050,
                    width: "260px",
                    backgroundColor: "var(--color-surface)",
                    borderRadius: "var(--radius-xl)",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
                    border: "1px solid var(--color-border)",
                    overflow: "hidden",
                    animation: "dropdownFadeIn 0.15s ease-out",
                  }}
                >
                  {/* Header com gradiente */}
                  <div
                    style={{
                      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      padding: "1rem",
                    }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="user-photo"
                        style={{ width: "48px", height: "48px", border: "2px solid rgba(255,255,255,0.4)", flexShrink: 0 }}
                      >
                        {perfilService.getPhotoURL(currentUser, churchUser) ? (
                          <img
                            src={perfilService.getPhotoURL(currentUser, churchUser)}
                            alt={perfilService.getDisplayName(currentUser, churchUser)}
                            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                          />
                        ) : null}
                        <div className="user-photo-fallback" style={{ display: perfilService.getPhotoURL(currentUser, churchUser) ? "none" : "flex", fontSize: "1.25rem" }}>
                          {perfilService.getDisplayName(currentUser, churchUser).charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="text-white fw-bold text-truncate" style={{ fontSize: "0.9rem" }}>
                          {perfilService.getDisplayName(currentUser, churchUser)}
                        </div>
                        <span
                          style={{
                            backgroundColor: "rgba(255,255,255,0.25)",
                            color: "white",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            padding: "0.15rem 0.55rem",
                            borderRadius: "2rem",
                            display: "inline-block",
                            marginTop: "0.2rem",
                          }}
                        >
                          {ROLE_LABELS[churchUser?.role] || "Membro"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Itens de navegação */}
                  <div style={{ padding: "0.5rem" }}>
                    {[
                      { icon: "home", label: "Início", color: "#667eea", bg: "rgba(102,126,234,0.1)", action: () => { navigate("/dashboard"); setShowDropdown(false); } },
                      { icon: "book-open", label: "Leitura Bíblica", color: "#10b981", bg: "rgba(16,185,129,0.1)", action: () => { navigate("/biblia"); setShowDropdown(false); } },
                      ...(hasPermission(churchUser.role, UserRole.SEGUNDO_TESOUREIRO) ? [{ icon: "dollar-sign", label: "Financeiro", color: "#f59e0b", bg: "rgba(245,158,11,0.1)", action: () => { navigate("/financeiro"); setShowDropdown(false); } }] : []),
                      ...(hasPermission(churchUser.role, UserRole.SEGUNDO_SECRETARIO) ? [{ icon: "users", label: "Membros", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)", action: () => { navigate("/membros"); setShowDropdown(false); } }] : []),
                    ].map((item) => (
                      <button key={item.label} onClick={item.action} style={navItemStyle}>
                        <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: item.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name={item.icon} size={15} color={item.color} />
                        </div>
                        <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>{item.label}</span>
                      </button>
                    ))}

                    <div style={{ margin: "0.3rem 0.25rem", borderTop: "1px solid var(--color-border)" }} />

                    {isGoogleUser && (
                      <button onClick={handleProfileClick} style={navItemStyle}>
                        <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: "rgba(59,130,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <Icon name="user" size={15} color="#3b82f6" />
                        </div>
                        <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>Editar Perfil</span>
                      </button>
                    )}

                    <button onClick={handleActivateNotifications} style={navItemStyle}>
                      <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name={getNotificationIcon()} size={15} color="#f59e0b" />
                      </div>
                      <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>{getNotificationText()}</span>
                      {notificationService.getPermissionStatus() === "granted" && notificationService.isTokenRegistered() && (
                        <div style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "var(--color-success)", marginLeft: "auto", flexShrink: 0 }} />
                      )}
                    </button>

                    <button onClick={() => { setShowDropdown(false); setShowThemeModal(true); }} style={navItemStyle}>
                      <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: "rgba(139,92,246,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="palette" size={15} color="#8b5cf6" />
                      </div>
                      <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>Tema</span>
                    </button>

                    <button onClick={() => { navigate("/"); setShowDropdown(false); }} style={navItemStyle}>
                      <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: "rgba(16,185,129,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="home" size={15} color="#10b981" />
                      </div>
                      <span style={{ fontSize: "0.85rem", fontWeight: 500 }}>Área de Visitantes</span>
                    </button>

                    <div style={{ margin: "0.3rem 0.25rem", borderTop: "1px solid var(--color-border)" }} />

                    <button
                      onClick={handleLogoutClick}
                      style={{
                        ...navItemStyle,
                        backgroundColor: "rgba(239,68,68,0.07)",
                        color: "var(--color-danger)",
                      }}
                    >
                      <div style={{ width: "30px", height: "30px", borderRadius: "var(--radius-md)", backgroundColor: "rgba(239,68,68,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Icon name="LogOut" size={15} color="var(--color-danger)" />
                      </div>
                      <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>Sair da Conta</span>
                    </button>
                  </div>
                </div>
              </>
            )}
            </div>
          </div>
        </div>
      </div>

      <ModalComponents />

      {/* Modal de Seleção de Tema */}
      <Modal
        isOpen={showThemeModal}
        onClose={() => setShowThemeModal(false)}
        title="Escolher Tema"
        size="sm"
      >
        <ThemeSelector onSelect={() => setShowThemeModal(false)} />
      </Modal>
    </header>
  );
};

export default Header;
